import React from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

type Size = 'sm' | 'lg';
type IconPosition = 'before' | 'after' | 'none';
type Variant = 'fill' | 'border';
type Width = 'fit-content' | 'fixed' | 'fill';
type Color = 'primary' | 'tertiary';
type Breakpoint = 'mobile' | 'desktop' | 'all';

export interface ButtonProps {
  label?: string;
  size?: Size;
  icon?: React.ReactNode;
  iconPosition?: IconPosition;
  variant?: Variant;
  width?: Width;
  color?: Color;
  onClick?: () => void;
  disabled?: boolean;
  floating?: boolean;
  breakpoint?: Breakpoint;
  className?: string;
  fixedWidth?: string;
  type?: 'button' | 'submit' | 'reset';
  linkUrl?: string;
  linkTarget?: '_blank' | '_self';
}
const Button: React.FC<ButtonProps> = ({
  label,
  size = 'lg',
  icon,
  iconPosition = 'none',
  variant = 'fill',
  width = 'fit-content',
  color = 'primary',
  onClick,
  disabled = false,
  floating = false,
  breakpoint = 'all',
  className = '',
  fixedWidth,
  type = 'button',
  linkUrl,
  linkTarget = '_blank'
}) => {
  const navigate = useNavigate();
  const isIconBefore = Boolean(icon) && iconPosition === 'before';
  const isIconAfter = Boolean(icon) && iconPosition === 'after';

  const btnClass = classNames(
    'btn',
    `btn-${size}`,
    `btn-${variant}`,
    `btn-${color}`,
    {
      'btn-icon-before': isIconBefore,
      'btn-icon-after': isIconAfter,
      'btn-floating': floating,
      [`btn-floating-${breakpoint}`]: floating,
      'btn-fill-width': width === 'fill',
      'btn-fit-content': width === 'fit-content',
      'btn-fixed-width': width === 'fixed',
    },
    className
  );

  const style = width === 'fixed' && fixedWidth ? { width: fixedWidth } : {};

  const handleBtnClick = () => {
    if (linkUrl) {
      const isPdf = linkUrl.toLowerCase().endsWith('.pdf');
        if (isPdf) {
          const anchor = document.createElement('a');
          anchor.href = linkUrl;
          anchor.download = linkUrl.split('/').pop() || 'file.pdf';
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        } else if (linkTarget === '_blank') {
          window.open(linkUrl, '_blank');
        } else {
          navigate(linkUrl);
        }
      } else if (onClick) {
        onClick();
      }
  };
  return (
    <button
      type={type}
      className={btnClass}
      onClick={handleBtnClick}
      disabled={disabled}
      style={style}
      aria-label={label}
    >
      {isIconBefore && <span className="btn-icon">{icon}</span>}
      {label && <span className="btn-label">{label}</span>}
      {isIconAfter && <span className="btn-icon">{icon}</span>}
    </button>
  );
};

export default Button;
